import type { SavedEntry, SiteLayoutActions } from 'siteLayout';
import {
  Entry,
  entryFacesSameZone,
  EntrySide,
  isDuplicateName,
  Layout,
  ReaderAssignment,
  Zone,
} from 'siteLayout';

/**
 * A zone or entry drawer holds its edits locally and writes them to the
 * draft on Save, in one command, so the drawer can refuse an entry whose
 * crossings have no reader before anything is sent.
 */

export type ZoneForm = {
  /** Null while the zone is being created. */
  id: string | null;
  name: string;
  /** The access groups granted this zone in this version. */
  accessGroupIds: string[];
};

export type EntryForm = {
  /** Null while the entry is being created. */
  id: string | null;
  name: string;
  sideA: EntrySide;
  sideB: EntrySide;
  /** Reader device ids admitting into each side. */
  readersA: string[];
  readersB: string[];
};

export type FormCheck = {
  errors: string[];
  /** Sides whose access needs a reader but has none. */
  invalidSideIds: string[];
};

const NEW_ENTRY_ID = 'new-entry';
const NEW_ZONE_ID = 'new-zone';

// ---- zones

export const zoneFormFrom = (zone: Zone): ZoneForm => ({
  id: zone.id,
  name: zone.name,
  accessGroupIds: [...(zone.accessGroupIds ?? [])],
});

export const blankZoneForm = (): ZoneForm => ({
  id: null,
  name: '',
  accessGroupIds: [],
});

/** The zone as the form has it, for rendering and checks. */
export const zoneOfForm = (form: ZoneForm): Zone => ({
  id: form.id ?? NEW_ZONE_ID,
  name: form.name,
  accessGroupIds: form.accessGroupIds,
});

/** The layout with the form's zone in place of the stored one. */
export const layoutWithZoneForm = (layout: Layout, form: ZoneForm): Layout => {
  const zone = zoneOfForm(form);
  const zones = form.id
    ? layout.zones.map((candidate) =>
        candidate.id === form.id ? zone : candidate,
      )
    : [...layout.zones, zone];
  return { ...layout, zones };
};

export const checkZoneForm = (layout: Layout, form: ZoneForm): FormCheck => {
  const errors: string[] = [];
  if (!form.name.trim()) errors.push('A zone needs a name.');
  else if (
    isDuplicateName(layoutWithZoneForm(layout, form).zones, zoneOfForm(form))
  )
    errors.push(`A zone named ${form.name.trim()} already exists.`);
  return { errors, invalidSideIds: [] };
};

const sameIds = (one: string[], other: string[]): boolean =>
  one.every((id) => other.includes(id)) &&
  other.every((id) => one.includes(id));

export const sameZoneForm = (one: ZoneForm, other: ZoneForm): boolean =>
  one.name === other.name && sameIds(one.accessGroupIds, other.accessGroupIds);

/**
 * A zone form over its zone as the draft now stores it (`fresh`), where the
 * form was built from `base`: a field someone changed meanwhile shows theirs,
 * and the rest keeps the form's edits.
 */
export const rebaseZoneForm = (
  form: ZoneForm,
  base: ZoneForm,
  fresh: ZoneForm,
): ZoneForm => ({
  ...form,
  name: fresh.name === base.name ? form.name : fresh.name,
  accessGroupIds: sameIds(fresh.accessGroupIds, base.accessGroupIds)
    ? form.accessGroupIds
    : fresh.accessGroupIds,
});

/** Writes the form's zone to the draft in one command, creating it if new. Its groups wait for M4. */
export const saveZoneForm = (
  actions: SiteLayoutActions,
  form: ZoneForm,
): Promise<void> =>
  actions.saveZone({ zoneId: form.id, name: form.name.trim() });

// ---- entries

const readersInto = (layout: Layout, sideId: string): string[] =>
  layout.readerAssignments
    .filter((assignment) => assignment.admitsIntoSideId === sideId)
    .map((assignment) => assignment.readerDeviceId);

export const entryFormFrom = (layout: Layout, entry: Entry): EntryForm => ({
  id: entry.id,
  name: entry.name,
  sideA: { ...entry.sideA },
  sideB: { ...entry.sideB },
  readersA: readersInto(layout, entry.sideA.id),
  readersB: readersInto(layout, entry.sideB.id),
});

/** Outside into the first zone, access enforced on the way in and none on the way out. */
export const blankEntryForm = (layout: Layout): EntryForm => ({
  id: null,
  name: '',
  sideA: {
    id: `${NEW_ENTRY_ID}-a`,
    position: 'A',
    kind: 'OUTSIDE',
    zoneId: null,
    enforcement: 'NOT_TRACKED',
  },
  sideB: {
    id: `${NEW_ENTRY_ID}-b`,
    position: 'B',
    kind: 'ZONE',
    zoneId: layout.zones[0]?.id ?? null,
    enforcement: 'ENFORCED',
  },
  readersA: [],
  readersB: [],
});

/** The entry as the form has it, for rendering and checks. */
export const entryOfForm = (form: EntryForm): Entry => ({
  id: form.id ?? NEW_ENTRY_ID,
  name: form.name,
  sideA: form.sideA,
  sideB: form.sideB,
});

/**
 * The layout with the form's entry and reader placements in place of the
 * stored ones, so the editor's pool of unplaced readers and its name checks
 * see the edits.
 */
export const layoutWithEntryForm = (
  layout: Layout,
  form: EntryForm,
  readerNameOf: (readerDeviceId: string) => string,
): Layout => {
  const entry = entryOfForm(form);
  const entries = form.id
    ? layout.entries.map((candidate) =>
        candidate.id === form.id ? entry : candidate,
      )
    : [...layout.entries, entry];
  const assignment = (
    readerDeviceId: string,
    side: EntrySide,
  ): ReaderAssignment => ({
    id: `${entry.id}-${readerDeviceId}`,
    readerId: readerDeviceId,
    readerDeviceId,
    readerName: readerNameOf(readerDeviceId),
    entryId: entry.id,
    admitsIntoSideId: side.id,
  });
  const readerAssignments = [
    ...layout.readerAssignments.filter(
      (candidate) => candidate.entryId !== entry.id,
    ),
    ...form.readersA.map((id) => assignment(id, form.sideA)),
    ...form.readersB.map((id) => assignment(id, form.sideB)),
  ];
  return { ...layout, entries, readerAssignments };
};

/** One side as the forms have it: what it faces, its access and its readers. */
const sameSide = (
  one: EntryForm,
  other: EntryForm,
  position: EntrySide['position'],
): boolean => {
  const [sideOne, sideOther] =
    position === 'A' ? [one.sideA, other.sideA] : [one.sideB, other.sideB];
  const [readersOne, readersOther] =
    position === 'A'
      ? [one.readersA, other.readersA]
      : [one.readersB, other.readersB];
  return (
    sideOne.kind === sideOther.kind &&
    sideOne.zoneId === sideOther.zoneId &&
    sideOne.enforcement === sideOther.enforcement &&
    sameIds(readersOne, readersOther)
  );
};

export const sameEntryForm = (one: EntryForm, other: EntryForm): boolean =>
  one.name === other.name &&
  sameSide(one, other, 'A') &&
  sameSide(one, other, 'B');

/** The readers the draft places on entries other than this one. */
const readersPlacedElsewhere = (
  layout: Layout,
  entryId: string | null,
): Set<string> =>
  new Set(
    layout.readerAssignments
      .filter((assignment) => assignment.entryId !== entryId)
      .map(({ readerDeviceId }) => readerDeviceId),
  );

/**
 * A new entry's form without the readers the draft now places on another
 * entry, so a save never takes one back unseen.
 */
export const withoutReadersPlacedElsewhere = (
  layout: Layout,
  form: EntryForm,
): EntryForm => {
  const placedElsewhere = readersPlacedElsewhere(layout, form.id);
  return {
    ...form,
    readersA: form.readersA.filter((id) => !placedElsewhere.has(id)),
    readersB: form.readersB.filter((id) => !placedElsewhere.has(id)),
  };
};

/**
 * An entry form over its entry as the draft now stores it (`fresh`, read
 * from `layout`), where the form was built from `base`. The name, and each
 * side with its readers, that someone changed meanwhile shows theirs; the
 * rest keeps the form's edits. A reader the form keeps that the draft now
 * places on another entry, or on this entry's other side as taken from the
 * draft, is left there, so a save never takes it back unseen.
 */
export const rebaseEntryForm = (
  layout: Layout,
  form: EntryForm,
  base: EntryForm,
  fresh: EntryForm,
): EntryForm => {
  const takeA = !sameSide(base, fresh, 'A');
  const takeB = !sameSide(base, fresh, 'B');
  const placedElsewhere = readersPlacedElsewhere(layout, fresh.id);
  const keep = (readers: string[], takenOtherSide: string[]): string[] =>
    readers.filter(
      (id) => !placedElsewhere.has(id) && !takenOtherSide.includes(id),
    );
  return {
    ...form,
    name: fresh.name === base.name ? form.name : fresh.name,
    sideA: takeA ? fresh.sideA : form.sideA,
    sideB: takeB ? fresh.sideB : form.sideB,
    readersA: takeA
      ? fresh.readersA
      : keep(form.readersA, takeB ? fresh.readersB : []),
    readersB: takeB
      ? fresh.readersB
      : keep(form.readersB, takeA ? fresh.readersA : []),
  };
};

export const checkEntryForm = (layout: Layout, form: EntryForm): FormCheck => {
  const errors: string[] = [];
  const invalidSideIds: string[] = [];
  const entry = entryOfForm(form);
  if (!form.name.trim()) errors.push('An entry needs a name.');
  else if (
    isDuplicateName(layoutWithEntryForm(layout, form, () => '').entries, entry)
  )
    errors.push(`An entry named ${form.name.trim()} already exists.`);
  if (entryFacesSameZone(entry)) errors.push('From and To are the same zone.');
  if (form.sideB.enforcement !== 'NOT_TRACKED' && !form.readersB.length) {
    errors.push(
      'The From → To crossing needs at least one reader, or its Access set to None.',
    );
    invalidSideIds.push(form.sideB.id);
  }
  if (form.sideA.enforcement !== 'NOT_TRACKED' && !form.readersA.length) {
    errors.push(
      'The To → From crossing needs at least one reader, or its Access set to None.',
    );
    invalidSideIds.push(form.sideA.id);
  }
  return { errors, invalidSideIds };
};

/**
 * Writes the form's entry to the draft whole, in one command: its name, both
 * sides and the readers admitting into each, creating it if new. A reader
 * placed on another entry moves here, and is answered among `movedReaders`.
 */
export const saveEntryForm = (
  actions: SiteLayoutActions,
  form: EntryForm,
): Promise<SavedEntry> =>
  actions.saveEntry({
    entryId: form.id,
    name: form.name.trim(),
    sideA: {
      kind: form.sideA.kind,
      zoneId: form.sideA.zoneId,
      enforcement: form.sideA.enforcement,
      readerDeviceIds: form.readersA,
    },
    sideB: {
      kind: form.sideB.kind,
      zoneId: form.sideB.zoneId,
      enforcement: form.sideB.enforcement,
      readerDeviceIds: form.readersB,
    },
  });
